import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Search, Filter, ChevronDown, X, SlidersHorizontal } from 'lucide-react';
import axios from 'axios';
import ProductCard from '../components/ui/ProductCard';
import ProductCardSkeleton from '../components/ui/ProductCardSkeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ShopPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  // Filter states
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');
  const [sortBy, setSortBy] = useState(searchParams.get('sort') || '');
  const [priceRange, setPriceRange] = useState({
    min: searchParams.get('min_price') || '',
    max: searchParams.get('max_price') || ''
  });

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API}/categories`);
        setCategories(response.data);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        
        if (searchQuery) params.append('search', searchQuery);
        if (selectedCategory) params.append('category', selectedCategory);
        if (sortBy) params.append('sort', sortBy);
        if (priceRange.min) params.append('min_price', priceRange.min);
        if (priceRange.max) params.append('max_price', priceRange.max);

        const response = await axios.get(`${API}/products?${params.toString()}`);
        setProducts(response.data);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [searchQuery, selectedCategory, sortBy, priceRange]);

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchQuery) params.set('search', searchQuery);
    if (selectedCategory) params.set('category', selectedCategory);
    if (sortBy) params.set('sort', sortBy);
    if (priceRange.min) params.set('min_price', priceRange.min);
    if (priceRange.max) params.set('max_price', priceRange.max);
    setSearchParams(params);
  }, [searchQuery, selectedCategory, sortBy, priceRange, setSearchParams]);

  const handleSearch = (e) => {
    e.preventDefault();
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCategory('');
    setSortBy('');
    setPriceRange({ min: '', max: '' });
  };

  const hasActiveFilters = searchQuery || selectedCategory || sortBy || priceRange.min || priceRange.max;

  return (
    <div className="min-h-screen bg-[#FFF8E7]" data-testid="shop-page">
      {/* Header */}
      <div className="bg-white border-b border-[#6B4F2A]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-[#574021] mb-4" data-testid="breadcrumb">
            <Link to="/" className="hover:text-[#1F7A63] transition-colors">Home</Link>
            <span className="text-[#6B4F2A]/30">/</span>
            <span className="text-[#2C1E0F] font-medium">Shop</span>
            {selectedCategory && (
              <>
                <span className="text-[#6B4F2A]/30">/</span>
                <span className="text-[#2C1E0F] font-medium capitalize">{selectedCategory.replace('-', ' ')}</span>
              </>
            )}
          </nav>

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl md:text-4xl font-black text-[#2C1E0F] tracking-tight" data-testid="shop-title">
                {selectedCategory ? selectedCategory.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'All Products'}
              </h1>
              <p className="text-[#574021] mt-1" data-testid="products-count">
                {products.length} products found
              </p>
            </div>

            {/* Search Bar */}
            <form onSubmit={handleSearch} className="flex-1 max-w-md" data-testid="search-form">
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search products..."
                  className="w-full pl-12 pr-4 py-3 rounded-full border border-[#6B4F2A]/20 bg-white focus:outline-none focus:ring-2 focus:ring-[#1F7A63] focus:border-transparent"
                  data-testid="shop-search-input"
                />
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#574021]/50" />
              </div>
            </form>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-8">
          {/* Filters Sidebar - Desktop */}
          <aside className="hidden lg:block w-64 flex-shrink-0" data-testid="filters-sidebar">
            <div className="bg-white rounded-2xl border border-[#6B4F2A]/10 p-6 sticky top-24">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-bold text-[#2C1E0F]">Filters</h3>
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="text-sm text-[#1F7A63] hover:underline"
                    data-testid="clear-filters-btn"
                  >
                    Clear all
                  </button>
                )}
              </div>

              {/* Categories */}
              <div className="filter-section">
                <h4 className="text-sm font-semibold text-[#2C1E0F] mb-4 uppercase tracking-wider">
                  Categories
                </h4>
                <div className="space-y-3">
                  <button
                    onClick={() => setSelectedCategory('')}
                    className={`block w-full text-left px-3 py-2 rounded-lg transition-colors ${
                      !selectedCategory ? 'bg-[#1F7A63]/10 text-[#1F7A63] font-medium' : 'text-[#574021] hover:bg-[#6B4F2A]/5'
                    }`}
                    data-testid="filter-category-all"
                  >
                    All Products
                  </button>
                  {categories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => setSelectedCategory(cat.id)}
                      className={`block w-full text-left px-3 py-2 rounded-lg transition-colors ${
                        selectedCategory === cat.id ? 'bg-[#1F7A63]/10 text-[#1F7A63] font-medium' : 'text-[#574021] hover:bg-[#6B4F2A]/5'
                      }`}
                      data-testid={`filter-category-${cat.id}`}
                    >
                      {cat.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Price Range */}
              <div className="filter-section">
                <h4 className="text-sm font-semibold text-[#2C1E0F] mb-4 uppercase tracking-wider">
                  Price Range
                </h4>
                <div className="flex gap-2">
                  <input
                    type="number"
                    value={priceRange.min}
                    onChange={(e) => setPriceRange(prev => ({ ...prev, min: e.target.value }))}
                    placeholder="Min"
                    className="w-full px-3 py-2 rounded-lg border border-[#6B4F2A]/20 focus:outline-none focus:ring-2 focus:ring-[#1F7A63] text-sm"
                    data-testid="filter-price-min"
                  />
                  <span className="text-[#574021] self-center">-</span>
                  <input
                    type="number"
                    value={priceRange.max}
                    onChange={(e) => setPriceRange(prev => ({ ...prev, max: e.target.value }))}
                    placeholder="Max"
                    className="w-full px-3 py-2 rounded-lg border border-[#6B4F2A]/20 focus:outline-none focus:ring-2 focus:ring-[#1F7A63] text-sm"
                    data-testid="filter-price-max"
                  />
                </div>
              </div>
            </div>
          </aside>

          {/* Main Content */}
          <main className="flex-1">
            {/* Mobile Filter & Sort Bar */}
            <div className="flex gap-4 mb-6 lg:mb-0 lg:hidden">
              <button
                onClick={() => setShowFilters(true)}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-white rounded-xl border border-[#6B4F2A]/20"
                data-testid="mobile-filter-btn"
              >
                <SlidersHorizontal className="w-5 h-5" />
                Filters
                {hasActiveFilters && (
                  <span className="w-2 h-2 bg-[#1F7A63] rounded-full" />
                )}
              </button>
            </div>

            {/* Sort Bar - Desktop */}
            <div className="hidden lg:flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                {hasActiveFilters && (
                  <div className="flex flex-wrap gap-2">
                    {selectedCategory && (
                      <span className="inline-flex items-center gap-1 px-3 py-1 bg-[#1F7A63]/10 text-[#1F7A63] rounded-full text-sm">
                        {selectedCategory.replace('-', ' ')}
                        <button onClick={() => setSelectedCategory('')} className="hover:text-[#2C1E0F]">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    )}
                    {searchQuery && (
                      <span className="inline-flex items-center gap-1 px-3 py-1 bg-[#1F7A63]/10 text-[#1F7A63] rounded-full text-sm">
                        "{searchQuery}"
                        <button onClick={() => setSearchQuery('')} className="hover:text-[#2C1E0F]">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    )}
                  </div>
                )}
              </div>

              <div className="flex items-center gap-3">
                <span className="text-sm text-[#574021]">Sort by:</span>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-[180px] bg-white" data-testid="sort-select">
                    <SelectValue placeholder="Default" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="newest" data-testid="sort-newest">Newest</SelectItem>
                    <SelectItem value="price_asc" data-testid="sort-price-asc">Price: Low to High</SelectItem>
                    <SelectItem value="price_desc" data-testid="sort-price-desc">Price: High to Low</SelectItem>
                    <SelectItem value="popular" data-testid="sort-popular">Popularity</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Products Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6" data-testid="products-grid">
              {loading
                ? [...Array(6)].map((_, i) => <ProductCardSkeleton key={i} />)
                : products.length > 0
                ? products.map((product) => (
                    <ProductCard key={product.id} product={product} />
                  ))
                : (
                    <div className="col-span-full text-center py-16" data-testid="no-products">
                      <p className="text-[#574021] text-lg mb-4">No products found</p>
                      <button
                        onClick={clearFilters}
                        className="btn-primary"
                      >
                        Clear Filters
                      </button>
                    </div>
                  )}
            </div>
          </main>
        </div>
      </div>

      {/* Mobile Filters Modal */}
      {showFilters && (
        <div className="fixed inset-0 z-50 lg:hidden" data-testid="mobile-filters-modal">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowFilters(false)} />
          <div className="absolute right-0 top-0 bottom-0 w-80 bg-white overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-bold text-[#2C1E0F] text-lg">Filters</h3>
                <button onClick={() => setShowFilters(false)}>
                  <X className="w-6 h-6 text-[#574021]" />
                </button>
              </div>

              {/* Sort */}
              <div className="mb-6 pb-6 border-b border-[#6B4F2A]/10">
                <h4 className="text-sm font-semibold text-[#2C1E0F] mb-4 uppercase tracking-wider">
                  Sort By
                </h4>
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-full bg-white">
                    <SelectValue placeholder="Default" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="newest">Newest</SelectItem>
                    <SelectItem value="price_asc">Price: Low to High</SelectItem>
                    <SelectItem value="price_desc">Price: High to Low</SelectItem>
                    <SelectItem value="popular">Popularity</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Categories */}
              <div className="mb-6 pb-6 border-b border-[#6B4F2A]/10">
                <h4 className="text-sm font-semibold text-[#2C1E0F] mb-4 uppercase tracking-wider">
                  Categories
                </h4>
                <div className="space-y-2">
                  <button
                    onClick={() => setSelectedCategory('')}
                    className={`block w-full text-left px-3 py-2 rounded-lg transition-colors ${
                      !selectedCategory ? 'bg-[#1F7A63]/10 text-[#1F7A63] font-medium' : 'text-[#574021]'
                    }`}
                  >
                    All Products
                  </button>
                  {categories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => setSelectedCategory(cat.id)}
                      className={`block w-full text-left px-3 py-2 rounded-lg transition-colors ${
                        selectedCategory === cat.id ? 'bg-[#1F7A63]/10 text-[#1F7A63] font-medium' : 'text-[#574021]'
                      }`}
                    >
                      {cat.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Price Range */}
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-[#2C1E0F] mb-4 uppercase tracking-wider">
                  Price Range
                </h4>
                <div className="flex gap-2">
                  <input
                    type="number"
                    value={priceRange.min}
                    onChange={(e) => setPriceRange(prev => ({ ...prev, min: e.target.value }))}
                    placeholder="Min"
                    className="w-full px-3 py-2 rounded-lg border border-[#6B4F2A]/20 focus:outline-none focus:ring-2 focus:ring-[#1F7A63] text-sm"
                  />
                  <span className="text-[#574021] self-center">-</span>
                  <input
                    type="number"
                    value={priceRange.max}
                    onChange={(e) => setPriceRange(prev => ({ ...prev, max: e.target.value }))}
                    placeholder="Max"
                    className="w-full px-3 py-2 rounded-lg border border-[#6B4F2A]/20 focus:outline-none focus:ring-2 focus:ring-[#1F7A63] text-sm"
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="flex-1 py-3 border border-[#6B4F2A]/20 rounded-full font-medium text-[#574021]"
                  >
                    Clear
                  </button>
                )}
                <button
                  onClick={() => setShowFilters(false)}
                  className="flex-1 btn-primary"
                >
                  Apply Filters
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShopPage;
